import type { PackageRow } from '@/services/api/packages';
import type {
  Item,
  Shipment,
  ShipmentClientSnapshot,
} from '@/services/api/shipments';

// Shipping-label printing for a whole shipment — the same label the Packages
// page prints (this folder's `LabelPreviewModal`), fed from a shipment's items
// instead of the packages list. Shared by the Shipments menu and the WMS
// outbound request detail, so the mapping has one source. Ported from
// react-link-web `shipmentToPackageRows`.

/**
 * Cap per bulk print. Each shipment expands to one label per item, so the PDF
 * grows fast; the BE `items:bulk` endpoint itself accepts up to 100 ids.
 */
export const MAX_BULK_LABEL_SHIPMENTS = 50;

/** Same rule as the Packages page: everything but a cancelled one prints. */
export const isShipmentLabelPrintable = (status: Shipment['status']): boolean =>
  status !== 'CANCELLED';

// Order the BE uses for the Packages page's itemSeq — (createdAt, id) — so a
// parcel gets the same "X / Y" whichever menu its label was printed from.
const byCreation = (a: Item, b: Item): number => {
  const at = a.createdAt ?? '';
  const bt = b.createdAt ?? '';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/**
 * One label row per live item. Cancelled items get no label and don't count
 * toward the "X / Y" position — matching the Packages page. Addresses fall
 * back from the item to its shipment.
 *
 * `fallbackClient` backs the label header ("DASH | {client}") for shipments
 * saved before the BE snapshotted `client`.
 */
export const shipmentToPackageRows = (
  shipment: Shipment,
  fallbackClient?: ShipmentClientSnapshot | null
): PackageRow[] => {
  const items = (shipment.items ?? [])
    .filter((item) => item.status !== 'CANCELLED')
    .sort(byCreation);
  const client = shipment.client ?? fallbackClient ?? undefined;

  return items.map((item, i) => ({
    id: item.id,
    po: item.invoice ?? '',
    deliveryId: shipment.waybill,
    bookingId: shipment.bookingId ?? undefined,
    outlet: shipment.receiverName ?? '',
    sender: shipment.senderName ?? '',
    client: client ? { id: client.id, name: client.name } : undefined,
    qty: item.quantity ?? 0,
    koli: item.koli ?? 1,
    // Numeric columns can arrive as strings — the Packages BE Number()s them too.
    weight: Number(item.weight ?? 0),
    volume: Number(item.volume ?? 0),
    status: item.status,
    recipientAddress:
      item.destinationAddress || shipment.destinationAddress || undefined,
    senderAddress: item.originAddress || shipment.originAddress || undefined,
    itemName: item.itemName ?? undefined,
    itemSeq: i + 1,
    itemTotal: items.length,
  }));
};
